import type { Evidence, InspectionStepId } from '@areapif/areapif-shared'
import { INSPECTION_STEPS, InspectionEvidenceType } from '@areapif/areapif-shared'

import type { EvidenceStepView } from './activeInspection'

/** One image of the "download all" zip: the storage key it is fetched from, and its name inside the zip. */
export interface EvidenceArchiveEntry {
  key: string
  name: string
}

/**
 * Each step's number in the zip: its position in the shared flow, counted from
 * one. Fixed across inspection levels, so `08` reads as precintos cierre in
 * every zip, whatever steps the level skips.
 */
const STEP_NUMBERS = new Map<InspectionStepId, number>(
  Object.values(INSPECTION_STEPS)
    .sort((a, b) => a.order - b.order)
    .map((step, index) => [step.id, index + 1]),
)

/** Evidence the timeline places on no step — the live page's pending seals — sorts after every step. */
const UNCLASSIFIED_NUMBER = 99
const UNCLASSIFIED_TOKEN = 'sin_clasificar'

const pad = (value: number, width: number): string => String(value).padStart(width, '0')

const extensionOf = (key: string): string => {
  const fileName = key.split('/').pop() ?? key
  const dot = fileName.lastIndexOf('.')
  return dot > 0 ? fileName.slice(dot) : ''
}

/**
 * The zip's entries, named `<container>_<NN>_<step>_<ii>.<ext>` so that sorting
 * the unzipped folder by name lists the images in the order the details page
 * renders them: step by step along the timeline, each step's photos before its
 * seals as its gallery shows them, then whatever the timeline places nowhere.
 *
 * `steps` is the timeline's step list; `files` is every storage key the zip
 * carries, in the order evidence left off the timeline should be listed. Each
 * key is named once, and only keys in `files` are named, so the zip holds
 * exactly the files it is handed.
 */
export const nameEvidenceArchive = (
  container: string,
  steps: Pick<EvidenceStepView, 'stepId' | 'evidence'>[],
  files: string[],
): EvidenceArchiveEntry[] => {
  const prefix = container.replace(/\s+/g, '_')
  const carried = new Set(files.filter(Boolean))
  const named = new Set<string>()
  const entries: EvidenceArchiveEntry[] = []

  const nameGroup = (number: number, token: string, keys: string[]) => {
    const unnamed = keys.filter(key => carried.has(key) && !named.has(key))
    const unique = [...new Set(unnamed)]
    // Every index of a step shares one width, so a step of 100 images still sorts.
    const width = Math.max(2, String(unique.length - 1).length)
    unique.forEach((key, index) => {
      named.add(key)
      entries.push({ key, name: `${prefix}_${pad(number, 2)}_${token}_${pad(index, width)}${extensionOf(key)}` })
    })
  }

  const pathsOf = (evidence: Evidence[], type: InspectionEvidenceType) => evidence.filter(item => item.type === type).map(item => item.path)

  steps.forEach(step => {
    const number = STEP_NUMBERS.get(step.stepId)
    // A step the shared flow does not know leaves its evidence to the unclassified group.
    if (number === undefined) return
    nameGroup(number, step.stepId.toLowerCase(), [
      ...pathsOf(step.evidence, InspectionEvidenceType.PHOTO),
      ...pathsOf(step.evidence, InspectionEvidenceType.SEAL),
    ])
  })

  nameGroup(UNCLASSIFIED_NUMBER, UNCLASSIFIED_TOKEN, files)

  return entries
}
